import React, { useCallback, useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';

import FeedbackModal from '../FeedbackModal';

import { addFeedbackedArticleId } from '../../utils/feedback';

import { sendArticleFeedback } from '../../utils/api';

import styles from './FeedbackButtons.module.scss';
import { getFeedbackByArticleId } from '../../utils/feedback';

const FeedbackButton = ({ type, handleClick, hasVoted, disabled }) => {
    const { t } = useTranslation('common');
    const rating = type === 'up-vote' ? 1 : 0;

    const buttonClasses = classNames(styles['FeedbackButtons__Button'], {
        [styles['FeedbackButtons__Button--DownVote']]: type === 'down-vote',
        [styles['FeedbackButtons__Button--HasVoted']]: hasVoted,
    });

    const handleClickWithRating = useCallback(
        () => handleClick(rating),
        [rating, handleClick]
    );

    return (
        <button
            className={buttonClasses}
            onClick={handleClickWithRating}
            disabled={disabled}>
            <span className="sr-only">
                {type === 'up-vote'
                    ? t('feedbackbuttons.giveupvote')
                    : t('feedbackbuttons.givedownvote')}
            </span>
        </button>
    );
};

FeedbackButton.propTypes = {
    type: PropTypes.oneOf(['up-vote', 'down-vote']),
    handleClick: PropTypes.func,
    disabled: PropTypes.bool,
    hasVoted: PropTypes.bool,
};

FeedbackButton.defaultProps = {
    type: 'up-vote',
    disabled: false,
    hasVoted: false,
};

const FeedbackButtons = ({ articleId }) => {
    const { t } = useTranslation('common');
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [feedbackId, setFeedbackId] = useState(null);
    const [feedbackSubmitted, setFeedbackSubmitted] = useState({
        submitted: false,
        rating: -1,
    });

    const toggleFeedbackModal = () => setFeedbackModalOpen(!feedbackModalOpen);

    const handleButtonClick = async (rating) => {
        setFeedbackSubmitted({ submitted: true, rating });

        try {
            const result = await sendArticleFeedback(articleId, rating);
            if (result.id) {
                // set id passed to feedback comment modal
                setFeedbackId(result.id);
                // save feedback state to storage
                addFeedbackedArticleId(articleId, rating);
            }
        } catch (e) {
            console.error('Error - could not submit feedback: ', e);
        }

        toggleFeedbackModal();
    };

    // check if user already has feedbacked article
    useEffect(() => {
        const feedback = getFeedbackByArticleId(articleId);
        if (feedback) {
            setFeedbackSubmitted({
                submitted: true,
                rating: feedback.rating,
            });
        }
    }, [articleId]);

    return (
        <div className={styles['FeedbackButtons']}>
            <span className={styles['FeedbackButtons__Label']}>
                {feedbackSubmitted.submitted
                    ? t('feedbackbuttons.thanksforfeedback')
                    : t('feedbackbuttons.giveusfeedback')}
            </span>
            <div className={styles['FeedbackButtons__ButtonWrapper']}>
                <FeedbackButton
                    type="up-vote"
                    handleClick={handleButtonClick}
                    hasVoted={feedbackSubmitted.rating === 1}
                    disabled={feedbackSubmitted.submitted}
                />
                <FeedbackButton
                    type="down-vote"
                    handleClick={handleButtonClick}
                    hasVoted={feedbackSubmitted.rating === 0}
                    disabled={feedbackSubmitted.submitted}
                />
            </div>

            <FeedbackModal
                feedbackId={feedbackId}
                visible={feedbackModalOpen}
                toggleVisible={toggleFeedbackModal}
            />
        </div>
    );
};

FeedbackButtons.propTypes = {
    articleId: PropTypes.number,
};

export default FeedbackButtons;
